import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { emailRule, requireRule } from "src/components/common/forms/validation.ts";
import i18n from "i18next";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { FormRichTextEditor } from "src/components/common/forms/fields/FormRichTextEditor.tsx";
import { useOAuthAuthentication } from "src/utils/oAuth.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { ObjectDocumentSendingApi } from "src/api/generated/tenantMail/api/objectDocumentSendingApi.ts";
import { AavoObjectType } from "src/api/generated/common/aavoObject/aavoObjectType.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { EmailTemplateSelectButton } from "src/components/views/email/EmailTemplateSelectButton.tsx";
import { FormFromMailField } from "src/components/views/tenantMail/FormFromMailField.tsx";
import { useRecentFromMailboxes } from "src/components/views/tenantMail/useRecentFromMailboxes.ts";
import { HorizontalBox } from "../../common/box/HorizontalBox.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SendObjectDocumentsViewProps {
	objectType: Extract<AavoObjectType, "CUSTOMER_ORDER" | "CUSTOMER_ORDER_OFFER" | "PURCHASE_ORDER">;
	objectId: number;
	onCompletedSuccessfully: () => void;
}

interface SendObjectDocumentsFormValues {
	mailFrom: string;
	mailReceiver: string;
	mailSubject: string;
	mailContent: string;
	saveMainDocument: boolean;
}

interface FormInitData {
	defaultReceiver: string;
	defaultSubject: string;
	mainDocumentFriendlyName: string;
	emailSignature: string | null | undefined;
}

export const SendObjectDocumentsView = ({
	objectType,
	objectId,
	onCompletedSuccessfully,
}: SendObjectDocumentsViewProps) => {
	const { checkAuthentication } = useOAuthAuthentication(ObjectDocumentSendingApi.ensureAuthentication);
	const { appUserSettings, supportsCustomFromAddress } = useGlobalInitData();
	const { currentUserEmail, recentFromMailboxes, defaultFromMailbox, rememberRecentMailbox, removeRecentMailbox } =
		useRecentFromMailboxes();

	return (
		<AsyncForm<FormInitData, SendObjectDocumentsFormValues, void>
			fetch={async () => {
				const formData = await ObjectDocumentSendingApi.getFormInitData({
					objectType: objectType,
					objectId: objectId,
				});
				return {
					...formData,
					emailSignature: appUserSettings?.emailSignature,
				};
			}}
			getDefaultValues={(data) => ({
				mailFrom: defaultFromMailbox,
				mailReceiver: data.defaultReceiver,
				mailSubject: data.defaultSubject,
				mailContent: data.emailSignature ? `<br/><br/>${data.emailSignature}` : "",
				saveMainDocument: false,
			})}
			submit={submitForm}
			submitLabel={i18n.t("send")}
			fitContent={true}
			columns={1}
			agentContext={{
				key: defineAgentUiContextKey("tenantMail.sendObjectDocuments.form"),
				data: { objectType, objectId },
			}}
			onCompleted={(result) => {
				if (result.type === "success") onCompletedSuccessfully();
			}}
			render={({ control, setValue, data: { emailSignature, mainDocumentFriendlyName } }) => (
				<>
					{supportsCustomFromAddress && (
						<FormFromMailField
							control={control}
							name={"mailFrom"}
							rules={emailRule()}
							currentUserEmail={currentUserEmail}
							recentMailboxes={recentFromMailboxes}
							onDeleteRecentMailbox={removeRecentMailbox}
						/>
					)}
					<FormTextField
						control={control}
						name={"mailReceiver"}
						rules={{
							...requireRule(),
							...emailRule(),
						}}
						tooltip={i18n.t("you_can_use_semicolon_to_separate_multiple_email_addresses")}
						label={i18n.t("receiver_mail")}
					/>
					<HorizontalBox
						sx={{
							gap: 1,
							alignSelf: "stretch",
						}}
					>
						<FormTextField
							control={control}
							name={"mailSubject"}
							rules={requireRule()}
							label={i18n.t("title")}
							sx={{
								flex: 1,
							}}
							autoFocus
						/>
						<EmailTemplateSelectButton
							onTemplateSelected={({ subject, body }) => {
								if (subject.trim() !== "") setValue("mailSubject", subject);
								setValue("mailContent", body + (emailSignature ? `<br/><br/>${emailSignature}` : ""));
							}}
						/>
					</HorizontalBox>
					<FormRichTextEditor
						control={control}
						name={"mailContent"}
						label={i18n.t("message")}
						sx={{
							flex: 1,
							gridColumn: "1 / -1",
						}}
					/>
					<FormCheckbox
						control={control}
						name={"saveMainDocument"}
						label={i18n.t("save_document_to_documents", {
							documentName: mainDocumentFriendlyName.toLowerCase() ?? "",
						})}
					/>
				</>
			)}
		/>
	);

	async function submitForm(values: SendObjectDocumentsFormValues) {
		const authResult = await checkAuthentication();
		if (authResult === "requiresAuthentication") {
			return "interrupted";
		}

		await ObjectDocumentSendingApi.sendDocuments({
			objectId: objectId,
			objectType: objectType,
			mailFrom: supportsCustomFromAddress ? values.mailFrom || undefined : undefined,
			mailSubject: values.mailSubject,
			mailContent: values.mailContent,
			mailReceiver: values.mailReceiver,
			saveMainDocument: values.saveMainDocument,
		});
		if (supportsCustomFromAddress) {
			rememberRecentMailbox(values.mailFrom);
		}
	}
};
